import type {
  UseFormReturn,
  SubmitHandler,
  UseFormProps,
  Path,
  FieldValues,
} from 'react-hook-form';
import type { Schema } from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

type ServerErrors<T> = {
  [Property in keyof T]: string | string[];
};
type FormProps<TFormValues extends FieldValues> = {
  onSubmit: SubmitHandler<TFormValues>;
  children: (methods: UseFormReturn<TFormValues>) => React.ReactNode;
  useFormProps?: UseFormProps<TFormValues>;
  validationSchema?: Schema<TFormValues> | any;
  serverError?: Partial<ServerErrors<TFormValues>> | null;
  resetValues?: any | null;
  className?: string;
  [key: string]: unknown;
};

export const Form = <
  TFormValues extends Record<string, any> = Record<string, any>,
>({
  onSubmit,
  children,
  useFormProps,
  validationSchema,
  serverError,
  resetValues,
  ...props
}: FormProps<TFormValues>) => {
  const methods = useForm<TFormValues>(
    //@ts-ignore
    {
      ...(!!validationSchema && { resolver: yupResolver(validationSchema) }),
      ...(!!useFormProps && useFormProps),
    },
  );
  
  const hasShownErrorToastRef = useRef(false);

  useEffect(() => {
    if (serverError) {
      Object.entries(serverError).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((message) => {
            methods.setError(key as Path<TFormValues>, {
              type: 'manual',
              message,
            });
          });
        } else {
          toast.error(value);
        }
      });
    }
  }, [serverError, methods]);

  useEffect(() => {
    if (methods?.formState?.isSubmitSuccessful && resetValues) {
      methods.reset(resetValues);
    }
  }, [methods?.formState?.isSubmitSuccessful]);

  // Scroll to first error and show toast notification
  useEffect(() => {
    const errors = methods.formState.errors;
    const isSubmitted = methods.formState.isSubmitted;

    if (isSubmitted && Object.keys(errors).length > 0) {
      // Show toast notification only once per submit attempt
      if (!hasShownErrorToastRef.current) {
        toast.error('Por favor, corrija os erros no formulário antes de continuar.');
        hasShownErrorToastRef.current = true;
      }

      // Get the first error field name
      const firstErrorField = Object.keys(errors)[0];
      
      // Try to find the element by ID or name
      let element = document.getElementById(firstErrorField);
      
      // If not found by ID, try to find by name attribute
      if (!element) {
        element = document.querySelector(`[name="${firstErrorField}"]`);
      }
      
      // If still not found, try to find the error message and scroll to its parent input
      if (!element) {
        const errorElement = document.querySelector(`[data-error="${firstErrorField}"]`);
        if (errorElement) {
          element = errorElement.closest('input, textarea, select, .phone-input-container') as HTMLElement;
        }
      }

      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
        });
        
        // Focus the element if it's an input
        if (element instanceof HTMLInputElement || 
            element instanceof HTMLTextAreaElement || 
            element instanceof HTMLSelectElement) {
          setTimeout(() => element?.focus(), 300);
        }
      }
    } else if (Object.keys(errors).length === 0) {
      // Reset the flag when errors are cleared (either by fixing them or successful submission)
      hasShownErrorToastRef.current = false;
    }
  }, [methods.formState.errors, methods.formState.isSubmitted]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} noValidate {...props}>
        {children(methods)}
      </form>
    </FormProvider>
  );
};
